<script lang="ts">
	import downloadIcon from "@assets/icons/download.svg?raw";
	import CkEditorContent from "@components/common/CkEditorContent.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import Media from "@components/common/Media.svelte";
	import { ButtonSize, ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import { Chromosome } from "@core/schema/Chromosome.js";
	import { ChromosomeLocationColor } from "@core/schema/ChromosomeLocationColor.js";
	import { chromosomeLocationColorColorMap } from "@core/schema/chromosomeLocationColorColorMap.js";
	import { getUploadedFilePath } from "@core/utils/getUploadedFilePath.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { productForContactForm } from "../state/productForContactForm.svelte.js";
	import type { ProductProps } from "./ProductProps.js";

	const { product, subcategory, manufacturer }: ProductProps = $props();
	const { FILE_BUCKET_URL_MAP } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();

	$effect(() => {
		productForContactForm.current = product.id;
	});

	const documents = $derived({
		information: product.informationDocument,
		safety: product.safetyDocument,
		guide: product.guide,
	});
	const hasDocument = $derived.by(() => {
		for (const document of Object.values(documents)) {
			if (Array.isArray(document)) {
				if (document.length > 0) {
					return true;
				}
			} else if (document !== undefined) {
				return true;
			}
		}
		return false;
	});
</script>

<Layout>
	<section class="pt-8 lg:pt-20 pb-24">
		<div class="flex flex-col lg:flex-row gap-12 lg:gap-28">
			<div class="lg:order-2">
				<div class="max-w-xl flex flex-col gap-8">
					<h1 class="max-md:text-xl">{product.name[tenant.locale]}</h1>
					<div class="max-w-lg leading-relaxed lg:leading-6 text-gray-600 flex flex-col gap-6">
						{#if product.content}
							<CkEditorContent class="order-1" html={product.content[tenant.locale]} />
						{/if}

						<Link
							class={["order-2 lg:order-last", product.mainPhoto ? "lg:self-end" : ""]}
							href="#contact-form"
							variant={ButtonVariant.Primary}
							size={ButtonSize.Base}>Poptat produkt</Link
						>
					</div>
				</div>
			</div>
			{#if product.mainPhoto}
				<div class="flex gap-12 lg:gap-20 lg:order-1">
					<div class="flex flex-col gap-8">
						<Media
							wrapperClass="order-3"
							class="rounded-2xl md:order-2 object-contain w-full h-full max-w-lg"
							height={512}
							width={512}
							alt={product.name[tenant.locale]}
							source={product.mainPhoto}
							eager
						/>
					</div>
				</div>
			{/if}
		</div>
		<div class="flex flex-col lg:flex-row gap-8 pt-8 lg:pt-20">
			<div>
				<span class="text-blue font-medium text-md mb-4 block">Základní informace</span>
				<div class="relative flex flex-col text-gray-700 bg-white shadow-sm max-w-xl rounded-xl">
					<table class="w-full text-left table-auto">
						<tbody class="text-left">
							<tr class="border-blue-gray-50 border-b">
								<th class="p-4">Typ produktu</th>
								<td class="p-4">{subcategory.name[tenant.locale]}</td>
							</tr>
							<tr class="border-blue-gray-50 border-b">
								<th class="p-4">Chromozomy</th>
								<td class="p-4">
									{Object.values(Chromosome)
										.filter((chromosome) => product.locations.some((location) => location.chromosome === chromosome))
										.join(", ")}
								</td>
							</tr>
							<tr class="border-blue-gray-50 border-b">
								<th class="p-4">Katalogové číslo</th>
								<td class="p-4">{product.catalogNumber}</td>
							</tr>
							<tr class="border-blue-gray-50 border-b">
								<th class="p-4">Štítky</th>
								<td class="p-4">
									<div class="flex flex-wrap items-center gap-2">
										{#each Object.values(ChromosomeLocationColor).filter( (color) => product.locations.some((location) => location.color === color), ) as color (color)}
											<div
												class="aspect-square h-4 rounded-full"
												style:background-color={chromosomeLocationColorColorMap[color]}
											></div>
										{/each}
									</div>
								</td>
							</tr>
							{#if manufacturer.name}
								<tr class="border-blue-gray-50 border-b">
									<th class="p-4">Výrobce</th>
									<td class="p-4">{manufacturer.name[tenant.locale]}</td>
								</tr>
							{/if}
						</tbody>
					</table>
				</div>
			</div>
			{#if hasDocument}
				<div>
					<span class="text-blue font-medium text-md mb-4 block">Dokumenty</span>
					<div class="relative flex flex-col text-gray-700 bg-white shadow-sm max-w-xl rounded-xl">
						<table class="w-full text-left table-auto">
							<tbody class="text-left">
								{#if documents.information}
									<tr>
										<th class="p-4 border-b border-blue-gray-50">Informační list</th>
										<td class="p-4 border-b border-blue-gray-50">
											<Link
												target="_blank"
												href={getUploadedFilePath(documents.information, FILE_BUCKET_URL_MAP)}
												variant={ButtonVariant.Outline}
												size={ButtonSize.Sm}
											>
												<Icon icon={downloadIcon} class="w-5" />
											</Link>
										</td>
									</tr>
								{/if}
								{#if documents.safety}
									<tr>
										<th class="p-4 border-b border-blue-gray-50">Bezpečnostní list</th>
										<td class="p-4 border-b border-blue-gray-50">
											<Link
												target="_blank"
												href={getUploadedFilePath(documents.safety, FILE_BUCKET_URL_MAP)}
												variant={ButtonVariant.Outline}
												size={ButtonSize.Sm}
											>
												<Icon icon={downloadIcon} class="w-5" />
											</Link>
										</td>
									</tr>
								{/if}
								{#if documents.guide}
									<tr>
										<th class="p-4 border-b border-blue-gray-50">Návod k použití</th>
										<td class="p-4 border-b border-blue-gray-50">
											<Link
												target="_blank"
												href={getUploadedFilePath(documents.guide, FILE_BUCKET_URL_MAP)}
												variant={ButtonVariant.Outline}
												size={ButtonSize.Sm}
											>
												<Icon icon={downloadIcon} class="w-5" />
											</Link>
										</td>
									</tr>
								{/if}
							</tbody>
						</table>
					</div>
				</div>
			{/if}
			{#if product.certificatesOfAnalysis.length > 0}
				{@const collator = new Intl.Collator(tenant.locale, { numeric: true, sensitivity: "base" })}
				<div>
					<span class="text-blue font-medium text-md mb-4 block">Certifikáty o analýze</span>
					<div class="relative flex flex-col text-gray-700 bg-white shadow-sm max-w-xl rounded-xl">
						<table class="w-full text-left table-auto">
							<tbody class="text-left">
								{#each product.certificatesOfAnalysis.toSorted( (first, second) => collator.compare(first.number ?? "", second.number ?? ""), ) as certificate (certificate.id)}
									<tr>
										<th class="p-4 border-b border-blue-gray-50">
											Šarže č. {certificate.number ?? "??"}
										</th>
										<td class="p-4 border-b border-blue-gray-50">
											<Link
												target="_blank"
												href={getUploadedFilePath(certificate.attachment, FILE_BUCKET_URL_MAP)}
												variant={ButtonVariant.Outline}
												size={ButtonSize.Sm}
											>
												<Icon icon={downloadIcon} class="w-5" />
											</Link>
										</td>
									</tr>
								{/each}
							</tbody>
						</table>
					</div>
				</div>
			{/if}
		</div>
	</section>
</Layout>
